/**
 * DTMBarChart — Single-series bar chart
 * Use for: positional allocation, single-metric comparisons, rankings
 */
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import { TOKENS, ChartFigure } from "./tokens";

export function DTMBarChart({
  data,           // [{ label, value, color?, highlight? }]
  title,
  subtitle,
  source,
  valueKey = "value",
  labelKey = "label",
  layout = "vertical",  // "vertical" (horizontal bars) or "horizontal" (vertical bars)
  height = 220,
  formatter = (v) => v,
  highlightIndex,
  colorKey,       // if set, use data[colorKey] for bar color
  defaultColor = TOKENS.green,
  showLabels = true,
  domain,
  className = "",
}) {
  const getColor = (entry, i) => {
    if (highlightIndex !== undefined && i === highlightIndex) return TOKENS.highlight;
    if (entry.highlight) return TOKENS.highlight;
    if (colorKey && entry[colorKey]) return entry[colorKey];
    if (entry.color) return entry.color;
    return defaultColor;
  };

  const isVertical = layout === "vertical";

  return (
    <ChartFigure title={title} subtitle={subtitle} source={source} className={className}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout={isVertical ? "vertical" : "horizontal"}
          margin={isVertical ? { left: 10, right: 40, top: 4, bottom: 4 } : { left: 0, right: 0, top: 8, bottom: 0 }}
        >
          {isVertical ? (
            <>
              <XAxis type="number" tick={{ fontSize: 10, fill: TOKENS.muted, fontFamily: TOKENS.mono }} domain={domain} tickFormatter={formatter} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey={labelKey} tick={{ fontSize: 11, fill: "#a3a3a3", fontFamily: TOKENS.body }} width={70} axisLine={false} tickLine={false} />
            </>
          ) : (
            <>
              <XAxis dataKey={labelKey} tick={{ fontSize: 10, fill: "#a3a3a3", fontFamily: TOKENS.body }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: TOKENS.muted, fontFamily: TOKENS.mono }} domain={domain} tickFormatter={formatter} axisLine={false} tickLine={false} />
            </>
          )}
          <Tooltip contentStyle={TOKENS.tooltipStyle} formatter={(v) => formatter(v)} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
          <Bar dataKey={valueKey} radius={isVertical ? [0, 3, 3, 0] : [3, 3, 0, 0]} maxBarSize={28}>
            {data.map((entry, i) => (
              <Cell key={i} fill={getColor(entry, i)} />
            ))}
            {showLabels && (
              <LabelList
                dataKey={valueKey}
                position={isVertical ? "right" : "top"}
                formatter={formatter}
                style={{ fontSize: 10, fill: "#a3a3a3", fontFamily: TOKENS.mono }}
              />
            )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartFigure>
  );
}
